import { Button, IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import AIIcon from 'resources/AIIcon'
import AIInfoIcon from 'resources/AIInfoIcon'
import React from 'react'
import { SPEC_INT_GRADIENT, SPEC_INT_GRADIENT_HOVER } from './specIntelligenceConstants'

const useStyles = makeStyles(() => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${pxToRem(10)} ${pxToRem(8)} ${pxToRem(10)} ${pxToRem(12)}`,
        borderBottom: '1px solid #E0E0E0',
        flexShrink: 0,
    },
    titleArea: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(8),
        minWidth: 0,
    },
    iconCircle: {
        width: pxToRem(30),
        height: pxToRem(30),
        borderRadius: '50%',
        backgroundColor: '#E3F2FD',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#1565C0',
    },
    titleText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    title: {
        fontSize: pxToRem(13),
        fontWeight: 600,
        color: '#04325B',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
    },
    headerButton: {
        color: '#314959',
        padding: pxToRem(4),
    },
    headerIcon: {
        width: pxToRem(18),
        height: pxToRem(18),
    },
    rerunButton: {
        background: SPEC_INT_GRADIENT,
        color: '#FFFFFF',
        fontSize: pxToRem(12),
        fontWeight: 500,
        textTransform: 'none',
        padding: `${pxToRem(3)} ${pxToRem(10)}`,
        minWidth: 0,
        '&:hover': {
            background: SPEC_INT_GRADIENT_HOVER,
        },
    },
    rerunIcon: {
        width: pxToRem(14),
        height: pxToRem(14),
    },
}))

/** Props for {@link SpecIntelligenceDrawerHeader}. */
export interface SpecIntelligenceDrawerHeaderProps {
    /** Called when the user clicks the info button. */
    onInfoOpen: () => void
    /** Called when the user clicks the close button. */
    onClose: () => void
    /** When provided, a Re-run button is shown in the header actions. */
    onRerun?: () => void
}

/**
 * Header bar for the Spec Intelligence drawer — displays the title, info button,
 * and close button.
 */
const SpecIntelligenceDrawerHeader = ({ onInfoOpen, onClose, onRerun }: SpecIntelligenceDrawerHeaderProps) => {
    const classes = useStyles()
    return (
        <div className={classes.header}>
            <div className={classes.titleArea}>
                <div className={classes.iconCircle} aria-hidden="true">
                    <AIIcon className={classes.headerIcon} />
                </div>
                <div className={classes.titleText}>
                    <Typography component="h2" className={classes.title}>
                        Insights
                    </Typography>
                </div>
            </div>
            <div className={classes.headerActions}>
                {onRerun && (
                    <Button
                        size="small"
                        onClick={onRerun}
                        className={classes.rerunButton}
                        aria-label="Re-run analysis"
                        startIcon={<AIIcon className={classes.rerunIcon} aria-hidden="true" />}
                        disableRipple
                        disableFocusRipple
                    >
                        Re-run Analysis
                    </Button>
                )}
                <Tooltip title="AI Disclaimer">
                    <IconButton
                        size="small"
                        onClick={onInfoOpen}
                        aria-label="Insights info"
                        className={classes.headerButton}
                        disableRipple
                        disableFocusRipple
                    >
                        <AIInfoIcon aria-hidden="true" />
                    </IconButton>
                </Tooltip>
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label="Close panel"
                    className={classes.headerButton}
                    disableRipple
                    disableFocusRipple
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>
        </div>
    )
}

export default SpecIntelligenceDrawerHeader
